import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "@/lib/useTranslations";
import type { KeyboardEvent } from "react";

import { ArticlesLibrarySearchForm } from "@/components/ui/admin/articles/ArticlesLibrarySearchForm";
import { ArticlesLibraryQuickAddRow } from "@/components/ui/admin/articles/ArticlesLibraryQuickAddRow";
import { LibraryResultsFreezeControl } from "@/components/ui/admin/LibraryResultsFreezeControl";
import { LibrarySortableHeader } from "@/components/ui/admin/LibrarySortableHeader";
import { LibraryChecklistProgress } from "@/components/ui/admin/LibraryChecklistProgress";
import { LibraryPagination } from "@/components/ui/admin/LibraryPagination";
import { LibraryAssigneeCell } from "@/components/ui/admin/LibraryAssigneeCell";
import { defaultLocale } from "@/i18n/config";
import type {
  ArticleAdminListItem,
  ArticleLibraryTab,
  ArticleLibraryTabCounts,
  ArticleLibrarySortState,
  ContentStatus,
} from "@/core/services/types/article.types";
import {
  ARTICLE_LIBRARY_TABS,
  buildArticleLibraryHref,
  buildArticleLibrarySortHref,
  getArticleEditorialChecklistProgress,
  getArticleSharePath,
} from "@/core/services/types/article.types";
import type { LibraryPageSize } from "@/core/services/types/library-pagination.types";
import type { LibrarySortDirection } from "@/core/services/types/library-sort.types";

interface ArticlesLibraryPanelProps {
  items: ArticleAdminListItem[];
  total: number;
  page: number;
  pageSize: LibraryPageSize;
  tab: ArticleLibraryTab;
  tabCounts: ArticleLibraryTabCounts;
  searchQuery: string;
  sort: ArticleLibrarySortState;
  isRefreshing?: boolean;
  isFrozen?: boolean;
  onToggleResultsFreeze?: () => void;
  canFreezeResults?: boolean;
}

function formatPublishedAt(value: string | null): string {
  if (!value) return "—";

  return new Intl.DateTimeFormat(defaultLocale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function formatCreatedAt(value: string): string {
  return new Intl.DateTimeFormat(defaultLocale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function StatusBadge({ article }: { article: ArticleAdminListItem }) {
  const t = useTranslations("articles.library.statuses");

  const styles: Record<ContentStatus, string> = {
    SIGNAL: "bg-violet-50 text-violet-700",
    DRAFT: "bg-neutral-100 text-neutral-600",
    PENDING_REVIEW: "bg-amber-50 text-amber-700",
    SCHEDULED: "bg-amber-50 text-amber-800",
    PUBLISHED: "bg-emerald-50 text-emerald-700",
    ARCHIVED: "bg-neutral-100 text-neutral-500",
  };

  return (
    <span
      className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${styles[article.status]}`}
    >
      {t(article.status)}
    </span>
  );
}

function buildLibraryHref(
  tab: ArticleLibraryTab,
  page: number,
  searchQuery: string,
  sort: ArticleLibrarySortState,
  currentPageSize: LibraryPageSize,
): string {
  return buildArticleLibraryHref({
    tab,
    page,
    pageSize: currentPageSize,
    searchQuery,
    sort,
  });
}

function getSortDirection(
  sort: ArticleLibrarySortState,
): LibrarySortDirection {
  return sort?.direction ?? "desc";
}

function openArticleFromKeyboard(
  event: KeyboardEvent<HTMLTableRowElement>,
  onOpen: () => void,
) {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    onOpen();
  }
}

export function ArticlesLibraryPanel({
  items,
  total,
  page,
  pageSize,
  tab,
  tabCounts,
  searchQuery,
  sort,
  isRefreshing = false,
  isFrozen = false,
  onToggleResultsFreeze,
  canFreezeResults = false,
}: ArticlesLibraryPanelProps) {
  const t = useTranslations("articles.library");
  const tCommon = useTranslations("common");
  const navigate = useNavigate();

  const buildPaginationHref = (targetPage: number, targetPageSize = pageSize) =>
    buildLibraryHref(tab, targetPage, searchQuery, sort, targetPageSize);

  return (
    <section className="flex w-full min-w-0 flex-col gap-4">
      <ArticlesLibrarySearchForm
        tab={tab}
        searchQuery={searchQuery}
        sort={sort}
        pageSize={pageSize}
      />

      <div className="w-full min-w-0 overflow-hidden rounded-xl border border-black/10 bg-white shadow-sm">
      <nav
        aria-label={t("statusNav")}
        className="border-b border-black/10"
      >
        <ul className="flex items-center gap-1 overflow-x-auto px-4">
          {ARTICLE_LIBRARY_TABS.map((tabStatus) => {
            const isActive = tab === tabStatus;

            return (
              <li key={tabStatus} className="shrink-0">
                <Link
                  to={buildArticleLibraryHref({
                    tab: tabStatus,
                    page: 1,
                    pageSize,
                    searchQuery,
                  })}
                  className={`inline-flex border-b-2 px-3 py-3 text-sm transition-colors ${
                    isActive
                      ? "border-whart-dark font-medium text-whart-text"
                      : "border-transparent text-neutral-600 hover:border-black/10 hover:text-whart-text"
                  }`}
                  aria-current={isActive ? "page" : undefined}
                >
                  {t(`statuses.${tabStatus}`)}
                  <span className="ml-1.5 text-neutral-400">
                    ({tabCounts[tabStatus]})
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="flex items-center justify-between gap-3 border-b border-black/10 px-4 py-3">
        <div className="inline-flex flex-wrap items-center gap-2 text-sm text-neutral-600">
          <p className="inline-flex items-center gap-2">
            {t("articleCount", { count: total })}
            {isRefreshing ? (
              <span
                aria-label="Chargement"
                className="inline-block size-3.5 animate-spin rounded-full border-2 border-zinc-900 border-r-transparent"
              />
            ) : null}
          </p>
          {isFrozen ? (
            <span className="inline-flex rounded-xl bg-amber-100 px-2 py-0.5 text-xs font-medium text-zinc-900">
              {tCommon("library.frozenResultsBadge")}
            </span>
          ) : null}
        </div>
        {onToggleResultsFreeze ? (
          <LibraryResultsFreezeControl
            isFrozen={isFrozen}
            onToggle={onToggleResultsFreeze}
            disabled={!isFrozen && !canFreezeResults}
          />
        ) : null}
      </div>

      <div className="w-full min-w-0 overflow-x-auto">
          <table className="w-full min-w-[64rem] table-fixed border-collapse text-left text-sm">
            <colgroup>
              <col className="w-[30%]" />
              <col className="w-[18%]" />
              <col className="w-[16%]" />
              <col className="w-[10%]" />
              <col className="w-[12%]" />
              <col className="w-[14%]" />
            </colgroup>
            <thead className="sticky top-0 z-10 bg-zinc-50">
              <tr className="border-b border-black/10 text-xs font-medium uppercase tracking-wider text-neutral-500">
                <LibrarySortableHeader
                  label={t("titleColumn")}
                  column="title"
                  activeColumn={sort?.column}
                  direction={getSortDirection(sort)}
                  href={buildArticleLibrarySortHref({
                    tab,
                    searchQuery,
                    pageSize,
                    sort,
                    nextColumn: "title",
                  })}
                  className="border-r border-black/5 px-4 py-3 text-left"
                />
                <LibrarySortableHeader
                  label={t("createdAtColumn")}
                  column="created_at"
                  activeColumn={sort?.column}
                  direction={getSortDirection(sort)}
                  href={buildArticleLibrarySortHref({
                    tab,
                    searchQuery,
                    pageSize,
                    sort,
                    nextColumn: "created_at",
                  })}
                  className="border-r border-black/5 px-4 py-3 text-left"
                />
                <LibrarySortableHeader
                  label={tab === "SCHEDULED" ? t("scheduledAt") : t("publishedAt")}
                  column="published_at"
                  activeColumn={sort?.column}
                  direction={getSortDirection(sort)}
                  href={buildArticleLibrarySortHref({
                    tab,
                    searchQuery,
                    pageSize,
                    sort,
                    nextColumn: "published_at",
                  })}
                  className="border-r border-black/5 px-4 py-3 text-left"
                />
                <th className="border-r border-black/5 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-neutral-500">
                  {tCommon("library.checklistColumn")}
                </th>
                <LibrarySortableHeader
                  label={tCommon("status")}
                  column="status"
                  activeColumn={sort?.column}
                  direction={getSortDirection(sort)}
                  href={buildArticleLibrarySortHref({
                    tab,
                    searchQuery,
                    pageSize,
                    sort,
                    nextColumn: "status",
                  })}
                  className="border-r border-black/5 px-4 py-3 text-left"
                />
                <LibrarySortableHeader
                  label={t("assignmentColumn")}
                  column="assignment"
                  activeColumn={sort?.column}
                  direction={getSortDirection(sort)}
                  href={buildArticleLibrarySortHref({
                    tab,
                    searchQuery,
                    pageSize,
                    sort,
                    nextColumn: "assignment",
                  })}
                  className="px-4 py-3 text-left"
                />
              </tr>
            </thead>
            <tbody>
              <ArticlesLibraryQuickAddRow />
              {items.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-sm text-neutral-500"
                  >
                    {searchQuery ? t("emptySearch") : t("empty")}
                  </td>
                </tr>
              ) : (
                items.map((article) => {
                const editHref = getArticleSharePath(article.id);
                const openArticle = () => navigate(editHref);

                return (
                <tr
                  key={article.id}
                  tabIndex={0}
                  role="link"
                  aria-label={t("editArticle", { title: article.title })}
                  className="cursor-pointer border-b border-black/5 transition-colors even:bg-zinc-50/50 hover:bg-amber-50/40 focus-visible:bg-amber-50/40 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-whart-dark"
                  onClick={openArticle}
                  onKeyDown={(event) =>
                    openArticleFromKeyboard(event, openArticle)
                  }
                >
                  <td className="border-r border-black/5 px-4 py-3 font-medium text-whart-text">
                    <span className="line-clamp-2">{article.title}</span>
                  </td>
                  <td className="border-r border-black/5 px-4 py-3 whitespace-nowrap text-neutral-600">
                    {formatCreatedAt(article.created_at)}
                  </td>
                  <td className="border-r border-black/5 px-4 py-3 whitespace-nowrap text-neutral-600">
                    {formatPublishedAt(article.published_at)}
                  </td>
                  <td className="border-r border-black/5 px-4 py-3">
                    <LibraryChecklistProgress
                      progress={getArticleEditorialChecklistProgress(article)}
                    />
                  </td>
                  <td className="border-r border-black/5 px-4 py-3">
                    <StatusBadge article={article} />
                  </td>
                  <td className="px-4 py-3">
                    <LibraryAssigneeCell assignee={article.assignee} />
                  </td>
                </tr>
                );
              })
              )}
            </tbody>
          </table>
        </div>

      <LibraryPagination
        page={page}
        pageSize={pageSize}
        total={total}
        buildHref={buildPaginationHref}
      />
      </div>
    </section>
  );
}
